import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import CreatableSelect from "react-select/creatable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import api from "../../api/API";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useAuth } from "../../hooks/useAuth";
import { TASK_PRIORITY, TASK_PRIORITY_OPTIONS } from "../../utils/taskPriority";
import TaskTimePanel from "../TaskTimePanel";
import "./TaskModal.css";

const parseCollection = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (response?.data?.status === false) {
    throw new Error(response?.data?.message || "Nao foi possivel carregar os dados.");
  }
  return response?.data?.records || response?.data?.data || [];
};

export default function TaskModal({
  show,
  onClose,
  task,
  defaultProject,
  defaultDueDate,
  allowInboxTask = false,
  onSaved,
  offcanvas = false,
}) {
  const { selectedWorkspaceId } = useWorkspace();
  const { user } = useAuth();
  const [descricao, setDescricao] = useState("");
  const [detalhe, setDetalhe] = useState("");
  const [vencimento, setVencimento] = useState("");
  const [prioridade, setPrioridade] = useState(String(TASK_PRIORITY.MEDIUM));

  const [idProjeto, setIdProjeto] = useState("");
  const [responsavel, setResponsavel] = useState("");

  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [subtarefas, setSubtarefas] = useState([]);
  const [novaSubtarefa, setNovaSubtarefa] = useState("");

  const [comentarios, setComentarios] = useState([]);
  const [novoComentario, setNovoComentario] = useState("");

  // Vez do pipeline de agentes (tag humano/IA). O bloco so aparece quando a
  // tarefa ja carrega uma dessas duas tags - tarefa fora do pipeline nao mostra
  // nada disso.
  const [notaPipeline, setNotaPipeline] = useState("");
  const [enviandoPipeline, setEnviandoPipeline] = useState(false);

  const [metasPdi, setMetasPdi] = useState([]);
  const [idMetaPdi, setIdMetaPdi] = useState("");

  const [estimativaMinutos, setEstimativaMinutos] = useState("");

  const editing = !!task;

  // -----------------------------------------
  // CARREGAR PROJETOS
  // -----------------------------------------
  const loadProjects = async () => {
    if (!selectedWorkspaceId) {
      setProjects([]);
      return;
    }

    const r = await api.get(`/projects?workspaceId=${selectedWorkspaceId}`);
    setProjects(parseCollection(r));
  };

  // -----------------------------------------
  // CARREGAR MEMBROS
  // -----------------------------------------
  const loadMembers = async (workspaceId = selectedWorkspaceId) => {
    if (!workspaceId) {
      setMembers([]);
      return;
    }

    const r = await api.get(`/workspaces/${workspaceId}/members`);
    setMembers(parseCollection(r));
  };

  const loadCustomFields = async (workspaceId = selectedWorkspaceId) => {
    if (!workspaceId) {
      setCustomFields([]);
      return;
    }

    const r = await api.get(`/workspaces/${workspaceId}/task-custom-fields`);
    setCustomFields(parseCollection(r));
  };

  const loadTags = async (workspaceId = selectedWorkspaceId) => {
    if (!workspaceId) {
      setTags([]);
      return;
    }

    const r = await api.get(`/workspaces/${workspaceId}/task-tags`);
    setTags(parseCollection(r));
  };

  // -----------------------------------------
  // CARREGAR METAS PDI
  // -----------------------------------------
  const loadMetasPdi = async () => {
    try {
      const r = await api.get('/pdi/metas');
      setMetasPdi((r?.data?.records || []).filter(m => !m.concluida));
    } catch {
      setMetasPdi([]);
    }
  };

  // -----------------------------------------
  // CARREGAR SUBTAREFAS
  // -----------------------------------------
  const loadSubtasks = async (taskId) => {
    const r = await api.get(`/tasks/subtasks/${taskId}`);
    setSubtarefas(r.data);
  };

  // -----------------------------------------
  // CARREGAR COMENTÁRIOS
  // -----------------------------------------
  const loadComments = async (taskId) => {
    const result = await api.get(`/tasks/comments/${taskId}`);
    setComentarios(result.data);
  };

  // -----------------------------------------
  // ABRIR MODAL
  // -----------------------------------------
  useEffect(() => {
    if (!show) return;

    loadProjects();
    loadMembers(task?.id_workspace || selectedWorkspaceId);
    loadCustomFields(task?.id_workspace || selectedWorkspaceId);
    loadTags(task?.id_workspace || selectedWorkspaceId);
    loadMetasPdi();

    if (editing) {
      setDescricao(task.descricao);
      setDetalhe(task.detalhe || "");
      setVencimento(task.vencimento ? task.vencimento.substring(0, 10) : "");
      setPrioridade(task.prioridade ? String(task.prioridade) : String(TASK_PRIORITY.MEDIUM));
      setIdProjeto(task.id_projeto);
      setResponsavel(task.id_usuario_responsavel || "");
      setCustomValues(
        (task.campos_customizados || []).reduce((acc, field) => {
          acc[field.id] = field.valor ?? "";
          return acc;
        }, {})
      );
      setSelectedTags((task.tags || []).map((tag) => String(tag.id)));
      setIdMetaPdi(task.id_meta_pdi ? String(task.id_meta_pdi) : "");
      setEstimativaMinutos(task.estimativa_minutos ?? "");

      loadSubtasks(task.id);
      loadComments(task.id);
    } else {
      // Nova tarefa
      setDescricao("");
      setDetalhe("");
      setVencimento(defaultDueDate || "");
      setPrioridade(String(TASK_PRIORITY.MEDIUM));
      setIdProjeto(defaultProject || "");
      setResponsavel(user?.id || "");
      setCustomValues({});
      setSelectedTags([]);
      setIdMetaPdi("");
      setEstimativaMinutos("");

      setSubtarefas([]);
      setComentarios([]);
    }
  }, [show, selectedWorkspaceId, task, defaultProject, defaultDueDate, user]);

  // -----------------------------------------
  // SALVAR TAREFA
  // -----------------------------------------
  const saveTask = async () => {
    if (!descricao.trim()) return alert("Informe uma descrição");
    if (!idProjeto && !allowInboxTask && !editing) return alert("Selecione um projeto");

    const fields = {
      descricao,
      detalhe,
      vencimento: vencimento || null,
      id_workspace: task?.id_workspace || selectedWorkspaceId,
      id_projeto: idProjeto || null,
      id_usuario_responsavel: responsavel || user?.id || null,
      prioridade: Number(prioridade || TASK_PRIORITY.MEDIUM),
      campos_customizados: customValues,
      tags: selectedTags.map(Number).filter(Boolean),
      id_meta_pdi: idMetaPdi ? Number(idMetaPdi) : null,
      // Campo vazio limpa a estimativa; o servidor so mexe nela porque a chave
      // veio no payload.
      estimativa_minutos: estimativaMinutos === "" ? null : Number(estimativaMinutos),
    };

    try {
      if (editing) {
        await api.put(`/tasks/${task.id}`, { fields });
      } else {
        await api.post("/tasks", { fields });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("Erro ao salvar a tarefa.");
    }
  };

  // -----------------------------------------
  // ADICIONAR SUBTAREFA
  // -----------------------------------------
  const addSubtask = async () => {
    if (!novaSubtarefa.trim()) return;

    await api.post("/tasks", {
      fields: {
        descricao: novaSubtarefa,
        id_workspace: task?.id_workspace || selectedWorkspaceId,
        id_projeto: idProjeto,
        id_usuario_responsavel: responsavel,
        id_tarefa: task.id,
      },
    });

    await loadSubtasks(task.id);
    setNovaSubtarefa("");
    onSaved?.();
  };

  // -----------------------------------------
  // CHECKLIST: CONCLUIR / DISPENSAR / REMOVER
  // -----------------------------------------
  // Toda alteração avisa a tela de trás (onSaved), porque o servidor pode ter
  // fechado ou reaberto a tarefa-pai por auto-conclusão.
  const toggleSubtask = async (sub) => {
    await api.put(`/tasks/${sub.id}`, { fields: { concluido: !sub.concluido } });
    await loadSubtasks(task.id);
    onSaved?.();
  };

  const dispensarSubtask = async (sub) => {
    await api.put(`/tasks/${sub.id}`, { fields: { nao_se_aplica: !sub.nao_se_aplica } });
    await loadSubtasks(task.id);
    onSaved?.();
  };

  const removerSubtask = async (sub) => {
    await api.delete(`/tasks/${sub.id}`);
    await loadSubtasks(task.id);
    onSaved?.();
  };

  // -----------------------------------------
  // ENVIAR COMENTÁRIO
  // -----------------------------------------
  const addComment = async () => {
    if (!novoComentario.trim()) return;

    await api.post(`/tasks/comments/${task.id}`, {
        fields: {comentario: novoComentario}
    });

    await loadComments(task.id);
    setNovoComentario("");
  };

  const createTag = async (tagName) => {
    const workspaceId = task?.id_workspace || selectedWorkspaceId;
    const normalizedName = String(tagName || "").trim();
    if (!workspaceId || !normalizedName) return null;

    try {
      const response = await api.post(`/workspaces/${workspaceId}/task-tags`, {
        nome: normalizedName,
        cor_hexa: "#e2e8f0",
      });
      const createdTag = response?.data?.record;

      await loadTags(workspaceId);
      if (createdTag?.id) {
        setSelectedTags((current) => Array.from(new Set([...current, String(createdTag.id)])));
      }

      return createdTag || null;
    } catch (error) {
      console.error("Erro ao criar tag:", error);
      alert("Erro ao criar a tag.");
      return null;
    }
  };

  const tagOptions = tags.map((tag) => ({
    value: String(tag.id),
    label: tag.nome,
    color: tag.cor_hexa || "#e2e8f0",
    textColor: tag.cor_texto_hexa || "#0f172a",
  }));

  const selectedTagOptions = tagOptions.filter((option) => selectedTags.includes(option.value));

  // "Vez" do pipeline de agentes: deriva da propria tag selecionada, sem
  // pedir mais uma chamada. Tarefa sem nenhuma das duas tags esta fora do
  // pipeline - o bloco nem aparece.
  const vezAtual = selectedTagOptions.some((option) => option.label?.toLowerCase() === "ia")
    ? "IA"
    : selectedTagOptions.some((option) => option.label?.toLowerCase() === "humano")
    ? "humano"
    : null;

  const alterarVezPipeline = async (novaVez) => {
    if (!editing || enviandoPipeline) return;

    setEnviandoPipeline(true);

    try {
      // A troca em si e a unica parte que pode realmente "dar errado" aqui.
      await api.patch(`/tasks/${task.id}/pipeline/vez`, {
        vez: novaVez,
        comentario: notaPipeline.trim() || undefined,
      });

      setNotaPipeline("");
    } catch (error) {
      console.error("Erro ao atualizar a vez do pipeline:", error);
      alert(
        error?.response?.data?.message ||
          "Nao foi possivel atualizar a vez da tarefa no pipeline."
      );
      setEnviandoPipeline(false);
      return;
    }

    // Daqui pra baixo e so recarregar a tela com o que ja foi salvo. Se uma
    // dessas falhar (ex.: /tasks/comments com erro 500), a troca continua
    // valendo no servidor - so a tela fica desatualizada ate reabrir o card.
    // Nao pode virar "nao foi possivel atualizar a vez": seria mentira.
    try {
      await loadTags(task.id_workspace || selectedWorkspaceId);
    } catch (error) {
      console.error("Vez trocada, mas falhou ao recarregar as tags:", error);
    }

    try {
      await loadComments(task.id);
    } catch (error) {
      console.error("Vez trocada, mas falhou ao recarregar os comentarios:", error);
    }

    try {
      const tagsAtualizadas = await api.get(`/tasks/${task.id}/tags`);
      setSelectedTags(parseCollection(tagsAtualizadas).map((tag) => String(tag.id)));
    } catch (error) {
      console.error("Vez trocada, mas falhou ao recarregar as tags da tarefa:", error);
    }

    setEnviandoPipeline(false);
  };

  const tagSelectStyles = {
    multiValue: (base, state) => ({
      ...base,
      backgroundColor: state.data.color || "#e2e8f0",
      borderRadius: 4,
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      color: state.data.textColor || "#0f172a",
      fontWeight: 600,
    }),
    multiValueRemove: (base, state) => ({
      ...base,
      color: state.data.textColor || "#64748b",
      ":hover": {
        backgroundColor: "rgba(15, 23, 42, 0.16)",
        color: state.data.textColor || "#0f172a",
      },
    }),
  };

  const setCustomValue = (fieldId, value) => {
    setCustomValues((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  const renderCustomFieldControl = (field) => {
    const value = customValues[field.id] ?? (field.tipo === "multi_select" ? [] : "");

    if (field.tipo === "boolean") {
      return (
        <Form.Check
          type="switch"
          label={field.nome}
          checked={value === true || value === "true"}
          onChange={(event) => setCustomValue(field.id, event.target.checked)}
        />
      );
    }

    if (field.tipo === "select") {
      return (
        <>
          <Form.Label>{field.nome}</Form.Label>
          <Form.Select value={value || ""} onChange={(event) => setCustomValue(field.id, event.target.value)}>
            <option value="">Selecione</option>
            {(field.opcoes || []).map((option) => (
              <option key={option.value || option.label} value={option.value || option.label}>
                {option.label || option.value}
              </option>
            ))}
          </Form.Select>
        </>
      );
    }

    if (field.tipo === "multi_select") {
      const selectedValues = Array.isArray(value) ? value : [];
      return (
        <>
          <Form.Label>{field.nome}</Form.Label>
          <Form.Select
            multiple
            value={selectedValues}
            onChange={(event) =>
              setCustomValue(
                field.id,
                Array.from(event.target.selectedOptions).map((option) => option.value)
              )
            }
          >
            {(field.opcoes || []).map((option) => (
              <option key={option.value || option.label} value={option.value || option.label}>
                {option.label || option.value}
              </option>
            ))}
          </Form.Select>
        </>
      );
    }

    if (field.tipo === "user") {
      return (
        <>
          <Form.Label>{field.nome}</Form.Label>
          <Form.Select value={value || ""} onChange={(event) => setCustomValue(field.id, event.target.value)}>
            <option value="">Selecione</option>
            {members.map((m) => (
              <option key={m.id_usuario ?? m.id} value={m.id_usuario ?? m.id}>
                {m.descricao || m.nome || m.usuario || m.email}
              </option>
            ))}
          </Form.Select>
        </>
      );
    }

    const inputType = field.tipo === "number" ? "number" : field.tipo === "date" ? "date" : field.tipo === "url" ? "url" : "text";

    return (
      <>
        <Form.Label>{field.nome}</Form.Label>
        <Form.Control
          type={inputType}
          value={value || ""}
          onChange={(event) => setCustomValue(field.id, event.target.value)}
        />
      </>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered={!offcanvas}
      dialogClassName={`task-modal-dialog${offcanvas ? " task-modal-offcanvas" : ""}`}
    >
      <Modal.Header closeButton>
        <Modal.Title>{editing ? "Editar Tarefa" : "Nova Tarefa"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* DESCRIÇÃO */}
        <Form.Group className="mt-2">
          <Form.Label>Descrição</Form.Label>
          <Form.Control
            type="text"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </Form.Group>

        {/* DETALHE */}
        <Form.Group className="mt-3">
          <Form.Label>Detalhes (Pode usar Markdown)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={detalhe}
            onChange={(e) => setDetalhe(e.target.value)}
          />
        </Form.Group>

        {/* PROJETO */}
        <Form.Group className="mt-3">
          <Form.Label>Projeto</Form.Label>
          <Form.Select
            value={idProjeto}
            onChange={(e) => {
              setIdProjeto(e.target.value);
            }}
          >
            <option value="">Selecione</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.descricao}
              </option>
            ))}
          </Form.Select>
          {allowInboxTask && !editing && (
            <Form.Text className="text-muted">
              Se deixar sem projeto, a tarefa sera criada no inbox do workspace para classificar depois.
            </Form.Text>
          )}
        </Form.Group>

        {/* RESPONSÁVEL */}
        <Form.Group className="mt-3">
          <Form.Label>Responsável</Form.Label>
          <Form.Select
            value={responsavel}
            onChange={(e) => setResponsavel(e.target.value)}
          >
            <option value="">Selecione</option>
            {members.map((m) => (
              <option key={m.id_usuario ?? m.id} value={m.id_usuario ?? m.id}>
                {m.descricao || m.nome || m.usuario || m.email}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* VENCIMENTO */}
        <Form.Group className="mt-3">
          <Form.Label>Data de Vencimento</Form.Label>
          <Form.Control
            type="date"
            value={vencimento || ""}
            onChange={(e) => setVencimento(e.target.value)}
          />
        </Form.Group>

        <Form.Group className="mt-3">
          <Form.Label>Prioridade</Form.Label>
          <Form.Select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
            {TASK_PRIORITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {/* Na edicao, a estimativa mora no painel de tempo, ao lado do que ja
            foi gasto - que e a unica leitura que a torna util. Aqui ela existe
            so para quem quer dimensionar a tarefa ja na criacao. */}
        {!editing && (
          <Form.Group className="mt-3">
            <Form.Label>Estimativa (minutos)</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="15"
              placeholder="Opcional. Ex.: 90"
              value={estimativaMinutos}
              onChange={(e) =>
                setEstimativaMinutos(e.target.value === "" ? "" : Number(e.target.value))
              }
            />
          </Form.Group>
        )}

        <Form.Group className="mt-3">
          <Form.Label>Tags</Form.Label>
          <CreatableSelect
            classNamePrefix="task-tags-select"
            isMulti
            options={tagOptions}
            value={selectedTagOptions}
            styles={tagSelectStyles}
            placeholder="Digite para buscar ou criar tags..."
            formatCreateLabel={(inputValue) => `Criar tag "${inputValue}"`}
            noOptionsMessage={() => "Nenhuma tag encontrada"}
            onChange={(options) => setSelectedTags((options || []).map((option) => String(option.value)))}
            onCreateOption={async (inputValue) => {
              const createdTag = await createTag(inputValue);
              if (createdTag?.id) {
                setTags((current) => {
                  const exists = current.some((tag) => String(tag.id) === String(createdTag.id));
                  return exists ? current : [...current, createdTag];
                });
              }
            }}
          />
        </Form.Group>

        {/* So aparece para tarefa que ja esta no pipeline de agentes (tem a
            tag humano ou IA). Trocar a tag pelo select generico de tags acima
            deixaria facil escolher as duas ao mesmo tempo, ou nenhuma -
            aqui a troca e sempre a exclusiva humano<->IA. */}
        {editing && vezAtual && (
          <Form.Group className="mt-3 pipeline-vez-group">
            <Form.Label>
              <FontAwesomeIcon icon={faRobot} className="me-1" /> Pipeline de agentes
            </Form.Label>
            <div className="pipeline-vez-toggle">
              <Button
                type="button"
                size="sm"
                variant={vezAtual === "humano" ? "warning" : "outline-secondary"}
                disabled={enviandoPipeline}
                onClick={() => alterarVezPipeline("humano")}
              >
                Com o humano
              </Button>
              <Button
                type="button"
                size="sm"
                variant={vezAtual === "IA" ? "primary" : "outline-secondary"}
                disabled={enviandoPipeline}
                onClick={() => alterarVezPipeline("IA")}
              >
                Enviar para o agente
              </Button>
            </div>
            <Form.Control
              as="textarea"
              rows={2}
              className="mt-2"
              placeholder="Nota de contexto para o agente (opcional) - o que foi ajustado, o que ele precisa saber..."
              value={notaPipeline}
              onChange={(e) => setNotaPipeline(e.target.value)}
            />
            <Form.Text muted>
              A nota vira comentario na tarefa junto com a troca. Deixe vazio se so a mudanca de vez ja basta.
            </Form.Text>
          </Form.Group>
        )}

        {metasPdi.length > 0 && (
          <Form.Group className="mt-3">
            <Form.Label>⚡ Meta PDI vinculada</Form.Label>
            <Form.Select value={idMetaPdi} onChange={(e) => setIdMetaPdi(e.target.value)}>
              <option value="">Nenhuma</option>
              {metasPdi.map(m => (
                <option key={m.id} value={m.id}>{m.titulo} ({m.area})</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Ao concluir essa tarefa, o progresso da meta será atualizado automaticamente.
            </Form.Text>
          </Form.Group>
        )}

        {customFields.length > 0 && (
          <>
            <hr />
            <h5>Campos customizados</h5>
            {customFields.map((field) => (
              <Form.Group className="mt-3" key={field.id}>
                {renderCustomFieldControl(field)}
              </Form.Group>
            ))}
          </>
        )}

        {/* CONTROLE DE TEMPO */}
        {editing && (
          <>
            <hr />
            <TaskTimePanel
              task={task}
              estimativaMinutos={estimativaMinutos}
              onEstimativaChange={setEstimativaMinutos}
            />
          </>
        )}

        {/* SUBTAREFAS */}
        {editing && (
          <>
            <hr />
            <div className="subtask-header">
              <h5>Checklist</h5>
              {subtarefas.length > 0 && (
                <span className="subtask-progress">
                  {subtarefas.filter((s) => s.concluido || s.nao_se_aplica).length}/
                  {subtarefas.length}
                </span>
              )}
            </div>

            <div className="subtask-input-container">
              <Form.Control
                type="text"
                placeholder="Novo passo..."
                value={novaSubtarefa}
                onChange={(e) => setNovaSubtarefa(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSubtask();
                  }
                }}
              />
              <Button onClick={addSubtask}>+</Button>
            </div>

            {subtarefas.map((s) => (
              <div
                key={s.id}
                className={`subtask-item ${s.nao_se_aplica ? "dispensada" : ""}`}
              >
                <Form.Check
                  type="checkbox"
                  checked={s.concluido}
                  disabled={s.nao_se_aplica}
                  onChange={() => toggleSubtask(s)}
                />

                <span className={s.concluido ? "feita" : ""}>{s.descricao}</span>

                {s.vencimento && (
                  <small className="subtask-prazo">
                    {new Date(s.vencimento).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  </small>
                )}

                {s.responsavel && <small className="subtask-dono">{s.responsavel}</small>}

                <button
                  type="button"
                  className="subtask-acao"
                  title={s.nao_se_aplica ? "Voltar a valer" : "Nao se aplica"}
                  onClick={() => dispensarSubtask(s)}
                >
                  ⃠
                </button>

                <button
                  type="button"
                  className="subtask-acao remover"
                  title="Remover passo"
                  onClick={() => removerSubtask(s)}
                >
                  ×
                </button>
              </div>
            ))}
          </>
        )}

        {/* COMENTÁRIOS */}
        {editing && (
          <>
            <hr />
            <h5>Comentários</h5>

            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Digite um comentário..."
              value={novoComentario}
              onChange={(e) => setNovoComentario(e.target.value)}
            />

            <Button className="mt-2" onClick={addComment}>
              Enviar Comentário
            </Button>

            <ul className="mt-3">
              {comentarios.map((c) => (
                <li key={c.id} className={`comment-item${c.autor_agente ? " comment-item-agente" : ""}`}>
                  <div className="comment-header">
                    {/* Comentario de agente nao tem usuario_nome (id_usuario e
                        nulo de proposito): o autor vem de autor_agente. */}
                    {c.autor_agente ? (
                      <strong>
                        <FontAwesomeIcon icon={faRobot} className="comment-icone-agente" /> {c.autor_agente}
                      </strong>
                    ) : (
                      <strong>{c.usuario_nome}</strong>
                    )}
                    <span>{new Date(c.criado_em).toLocaleString()}</span>
                  </div>

                  <div
                    className="comment-body"
                    dangerouslySetInnerHTML={{
                      __html: window.marked?.parse(c.comentario || "") || c.comentario,
                    }}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
        <Button variant="primary" onClick={saveTask}>
          {editing ? "Salvar Alterações" : "Criar Tarefa"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
